import { Button, Stack, Typography } from "@mui/material";
import { NavLink as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  BookingFollowUpType,
  Employee,
} from "../../process/types/processTypes";
import { FollowUpStatusChip } from "./FollowUpStatusChip";
import {
  effectiveFollowUpStatus,
  getFollowUpForType,
} from "../utils/followUpHelpers";

const SUMMARY_TYPES: { type: BookingFollowUpType; labelKey: string }[] = [
  { type: BookingFollowUpType.medical, labelKey: "followUps.medical" },
  { type: BookingFollowUpType.tasheer, labelKey: "followUps.tasheer" },
  { type: BookingFollowUpType.embassy, labelKey: "followUps.embassy" },
  { type: BookingFollowUpType.lmis, labelKey: "followUps.lmis" },
  { type: BookingFollowUpType.flight, labelKey: "followUps.flight" },
];

interface Props {
  employee: Employee;
}

export function EmployeeFollowUpSummary({ employee }: Props) {
  const { t } = useTranslation();

  const rows = SUMMARY_TYPES.map(({ type, labelKey }) => ({
    type,
    label: t(labelKey),
    status: effectiveFollowUpStatus(getFollowUpForType(employee, type)),
    scheduledDate: getFollowUpForType(employee, type)?.scheduledDate,
  })).filter((row) => row.status !== undefined);

  if (rows.every((r) => r.status === "new" && !r.scheduledDate)) {
    return null;
  }

  return (
    <Stack spacing={1} sx={{ pt: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">{t("followUps.title")}</Typography>
        <Button component={RouterLink} to="/app/follow-ups" size="small">
          {t("followUps.manage")}
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {rows.map((row) => (
          <Stack key={row.type} spacing={0.25} alignItems="flex-start">
            <Typography variant="caption" color="text.secondary">
              {row.label}
            </Typography>
            <FollowUpStatusChip status={row.status} />
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}
